import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getDashboardSettings, updateDashboardSettings, getAIConfig, updateAIConfig, testAIConfig, getAllUserCredits, updateUserCredits, bulkUpdateCredits } from "@/lib/api";
import api from "@/lib/api";
import { Save, Loader2, Calendar, ExternalLink, Mail, Server, Shield, Eye, EyeOff, BotMessageSquare, Presentation, Key, Zap, CheckCircle, XCircle, Coins, Users, Clock, Globe, Database, UserCog } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    live_projects: 80,
    total_technical_experts: 60,
    calendly_url: "",
    odoo_url: "",
    odoo_db: "",
    odoo_username: "",
    odoo_api_key: "",
    odoo_default_duration: 30,
    odoo_available_hours_start: "09:00",
    odoo_available_hours_end: "18:00",
    odoo_assigned_user: "",
    odoo_enabled: false,
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_to_email: "",
    smtp_enabled: false,
    ai_consultant_enabled: true,
    ai_sales_coach_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // AI Config state
  const [aiConfig, setAiConfig] = useState({ provider: "openai", api_key: "", model_name: "gpt-4o" });
  const [aiConfigLoading, setAiConfigLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [savingAI, setSavingAI] = useState(false);

  // Credits state
  const [userCredits, setUserCredits] = useState([]);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [editingCredits, setEditingCredits] = useState({});
  const [defaultCredits, setDefaultCredits] = useState(50);
  const [odooTesting, setOdooTesting] = useState(false);
  const [odooTestResult, setOdooTestResult] = useState(null);
  const [showOdooKey, setShowOdooKey] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getDashboardSettings();
        setSettings(prev => ({ ...prev, ...res.data }));
      } catch (error) {
        console.error("Failed to load settings:", error);
      } finally {
        setLoading(false);
      }
    };
    const fetchAIConfig = async () => {
      try {
        const res = await getAIConfig();
        setAiConfig({ provider: res.data.provider || "openai", api_key: "", model_name: res.data.model_name || "gpt-4o", has_key: res.data.has_key, api_key_masked: res.data.api_key_masked });
      } catch (error) {
        console.error("Failed to load AI config:", error);
      }
      finally { setAiConfigLoading(false); }
    };
    const fetchCredits = async () => {
      try {
        const res = await getAllUserCredits();
        setUserCredits(res.data);
      } catch (error) {
        console.error("Failed to load credits:", error);
      }
      finally { setCreditsLoading(false); }
    };
    fetchSettings();
    fetchAIConfig();
    fetchCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDashboardSettings(settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const PROVIDER_MODELS = {
    openai: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"],
    anthropic: ["claude-sonnet-4-20250514", "claude-3-5-sonnet-20241022", "claude-3-haiku-20240307"],
    google: ["gemini-2.0-flash", "gemini-1.5-pro", "gemini-1.5-flash"]
  };

  const handleSaveAIConfig = async () => {
    setSavingAI(true);
    try {
      await updateAIConfig({ provider: aiConfig.provider, api_key: aiConfig.api_key, model_name: aiConfig.model_name });
      toast.success("AI configuration saved!");
      setTestResult(null);
      // Refresh to get masked key
      const res = await getAIConfig();
      setAiConfig(prev => ({ ...prev, has_key: res.data.has_key, api_key_masked: res.data.api_key_masked, api_key: "" }));
    } catch { toast.error("Failed to save AI config"); }
    finally { setSavingAI(false); }
  };

  const handleTestAI = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testAIConfig();
      setTestResult(res.data);
      if (res.data.success) toast.success("Connection successful!");
      else toast.error(`Test failed: ${res.data.error}`);
    } catch (e) { toast.error("Test failed"); setTestResult({ success: false, error: e.message }); }
    finally { setTesting(false); }
  };

  const handleSaveCredits = async (userId) => {
    const newCredits = editingCredits[userId];
    if (newCredits === undefined) return;
    try {
      await updateUserCredits(userId, parseInt(newCredits));
      setUserCredits(prev => prev.map(u => u.id === userId ? { ...u, credits: parseInt(newCredits) } : u));
      setEditingCredits(prev => { const n = { ...prev }; delete n[userId]; return n; });
      toast.success("Credits updated!");
    } catch { toast.error("Failed to update credits"); }
  };

  const handleBulkCredits = async () => {
    try {
      const res = await bulkUpdateCredits(defaultCredits);
      toast.success(res.data.message);
      const refresh = await getAllUserCredits();
      setUserCredits(refresh.data);
    } catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout title="Dashboard Settings">
      <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
        <p className="text-slate-600">Configure dashboard metrics, integrations, and email notifications</p>

        {/* AI Tools Toggle */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">AI Tools</CardTitle>
            <CardDescription>Enable or disable AI-powered tools for all users</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <BotMessageSquare className="w-5 h-5 text-[#0066CC]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">AI Consultant</p>
                      <p className="text-xs text-slate-500">AI-powered problem solving and consultation</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.ai_consultant_enabled}
                    onCheckedChange={(val) => handleChange('ai_consultant_enabled', val)}
                    data-testid="toggle-ai-consultant"
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <Presentation className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">AI Sales Coach</p>
                      <p className="text-xs text-slate-500">AI-powered sales training and coaching</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.ai_sales_coach_enabled}
                    onCheckedChange={(val) => handleChange('ai_sales_coach_enabled', val)}
                    data-testid="toggle-ai-sales-coach"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* AI API Configuration */}
        <Card className="border-slate-200" data-testid="ai-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="h-5 w-5 text-[#0066CC]" />
              AI Configuration
            </CardTitle>
            <CardDescription>Configure the AI provider and API key used by AI Consultant and Sales Coach</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiConfigLoading ? (
              <div className="flex items-center justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                {/* Provider */}
                <div className="space-y-2">
                  <Label>AI Provider</Label>
                  <Select value={aiConfig.provider} onValueChange={(val) => {
                    setAiConfig(prev => ({ ...prev, provider: val, model_name: PROVIDER_MODELS[val]?.[0] || "" }));
                    setTestResult(null);
                  }}>
                    <SelectTrigger data-testid="ai-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI (GPT)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                      <SelectItem value="google">Google (Gemini)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Model */}
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={aiConfig.model_name} onValueChange={(val) => setAiConfig(prev => ({ ...prev, model_name: val }))}>
                    <SelectTrigger data-testid="ai-model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(PROVIDER_MODELS[aiConfig.provider] || []).map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Or type a custom model name below</p>
                  <Input value={aiConfig.model_name} onChange={(e) => setAiConfig(prev => ({ ...prev, model_name: e.target.value }))}
                    placeholder="Custom model name" className="placeholder:text-slate-300" data-testid="ai-model-input" />
                </div>

                {/* API Key */}
                <div className="space-y-2">
                  <Label>API Key</Label>
                  {aiConfig.has_key && (
                    <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Key configured: {aiConfig.api_key_masked}</p>
                  )}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input type={showApiKey ? "text" : "password"}
                        value={aiConfig.api_key} onChange={(e) => setAiConfig(prev => ({ ...prev, api_key: e.target.value }))}
                        placeholder={aiConfig.has_key ? "Enter new key to update..." : "Enter your API key"}
                        className="pr-10 placeholder:text-slate-300" data-testid="ai-api-key-input" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {aiConfig.provider === "openai" && "Get your key from platform.openai.com/api-keys"}
                    {aiConfig.provider === "anthropic" && "Get your key from console.anthropic.com/settings/keys"}
                    {aiConfig.provider === "google" && "Get your key from aistudio.google.com/apikey"}
                  </p>
                </div>

                {/* Test Result */}
                {testResult && (
                  <div className={`p-3 rounded-lg border text-sm ${testResult.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                    <div className="flex items-center gap-2 font-medium">
                      {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {testResult.success ? "Connection successful!" : "Connection failed"}
                    </div>
                    {testResult.success && <p className="text-xs mt-1">Provider: {testResult.provider} | Model: {testResult.model}</p>}
                    {testResult.error && <p className="text-xs mt-1">{testResult.error}</p>}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleSaveAIConfig} disabled={savingAI} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-ai-config-btn">
                    {savingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save AI Config
                  </Button>
                  <Button variant="outline" onClick={handleTestAI} disabled={testing} data-testid="test-ai-config-btn">
                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Credits Management */}
        <Card className="border-slate-200" data-testid="credits-management-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Coins className="h-5 w-5 text-amber-500" />
              Credits Management
            </CardTitle>
            <CardDescription>Manage AI query credits for each user. 1 credit = 1 AI query.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {creditsLoading ? (
              <div className="flex items-center justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="flex items-end gap-3 p-3 bg-slate-50 rounded-lg border">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Default Credits for New Users</Label>
                    <Input type="number" min="0" value={defaultCredits} onChange={(e) => setDefaultCredits(parseInt(e.target.value) || 0)}
                      className="w-32" data-testid="default-credits-input" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleBulkCredits} data-testid="apply-default-credits-btn">
                    Apply to Unset Users
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Queries Used</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userCredits.map(u => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="text-sm font-medium">{u.name || u.email.split("@")[0]}</div>
                          <div className="text-xs text-slate-400">{u.email}</div>
                        </TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{u.role}</span></TableCell>
                        <TableCell>
                          {editingCredits[u.id] !== undefined ? (
                            <Input type="number" min="0" className="w-20 h-8 text-sm"
                              value={editingCredits[u.id]}
                              onChange={(e) => setEditingCredits(prev => ({ ...prev, [u.id]: e.target.value }))}
                              data-testid={`credit-input-${u.id}`} />
                          ) : (
                            <span className={`font-bold text-sm ${u.credits <= 5 ? "text-red-600" : u.credits <= 20 ? "text-amber-600" : "text-green-600"}`}>
                              {u.credits}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{u.total_queries}</TableCell>
                        <TableCell className="text-xs text-slate-400">{u.last_used ? new Date(u.last_used).toLocaleDateString() : "-"}</TableCell>
                        <TableCell>
                          {editingCredits[u.id] !== undefined ? (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 text-green-600" onClick={() => handleSaveCredits(u.id)} data-testid={`save-credits-${u.id}`}>
                                <Save className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-slate-400" onClick={() => setEditingCredits(prev => { const n = { ...prev }; delete n[u.id]; return n; })}>
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-7 text-[#0066CC]"
                              onClick={() => setEditingCredits(prev => ({ ...prev, [u.id]: u.credits }))}
                              data-testid={`edit-credits-${u.id}`}>
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>

        {/* Dashboard Metrics */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Dashboard Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Live Projects Count</Label>
                  <Input
                    type="number"
                    min="0"
                    value={settings.live_projects}
                    onChange={(e) => handleChange("live_projects", parseInt(e.target.value) || 0)}
                    data-testid="live-projects-input"
                  />
                  <p className="text-sm text-slate-500">This number is displayed in the dashboard metrics section</p>
                </div>
                <div className="space-y-2">
                  <Label>Default Technical Experts Count</Label>
                  <Input
                    type="number"
                    min="0"
                    value={settings.total_technical_experts}
                    onChange={(e) => handleChange("total_technical_experts", parseInt(e.target.value) || 0)}
                    data-testid="experts-input"
                  />
                  <p className="text-sm text-slate-500">Fallback number if no department data is available.</p>
                </div>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-settings-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Settings
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Odoo Calendar Integration */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-[#0066CC]" />
              Odoo Calendar Integration
            </CardTitle>
            <CardDescription>Connect to your Odoo ERP to enable "Schedule a Demo" booking directly into Odoo Calendar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Enable Odoo Calendar</p>
                    <p className="text-xs text-slate-500">Turn on to allow demo booking via Odoo</p>
                  </div>
                  <Switch checked={settings.odoo_enabled || false} onCheckedChange={(v) => handleChange("odoo_enabled", v)} data-testid="odoo-enable-toggle" />
                </div>

                {settings.odoo_enabled && (
                  <div className="space-y-4">
                    {/* Connection Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-slate-400" /> Odoo URL *</Label>
                        <Input
                          placeholder="https://your-company.odoo.com"
                          value={settings.odoo_url || ""}
                          onChange={(e) => handleChange("odoo_url", e.target.value)}
                          data-testid="odoo-url-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5"><Database className="h-3.5 w-3.5 text-slate-400" /> Database Name *</Label>
                        <Input
                          placeholder="your_database"
                          value={settings.odoo_db || ""}
                          onChange={(e) => handleChange("odoo_db", e.target.value)}
                          data-testid="odoo-db-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> Username (Email) *</Label>
                        <Input
                          placeholder="admin@company.com"
                          value={settings.odoo_username || ""}
                          onChange={(e) => handleChange("odoo_username", e.target.value)}
                          data-testid="odoo-username-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5"><Key className="h-3.5 w-3.5 text-slate-400" /> API Key *</Label>
                        <div className="relative">
                          <Input
                            type={showOdooKey ? "text" : "password"}
                            placeholder="Your Odoo API key"
                            value={settings.odoo_api_key || ""}
                            onChange={(e) => handleChange("odoo_api_key", e.target.value)}
                            className="pr-10"
                            data-testid="odoo-apikey-input"
                          />
                          <button onClick={() => setShowOdooKey(!showOdooKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showOdooKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Calendar Preferences */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-slate-400" /> Calendar Preferences
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Default Duration (min)</Label>
                          <Input
                            type="number"
                            min="15"
                            max="120"
                            step="15"
                            value={settings.odoo_default_duration || 30}
                            onChange={(e) => handleChange("odoo_default_duration", parseInt(e.target.value) || 30)}
                            data-testid="odoo-duration-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Available From</Label>
                          <Input
                            type="time"
                            value={settings.odoo_available_hours_start || "09:00"}
                            onChange={(e) => handleChange("odoo_available_hours_start", e.target.value)}
                            data-testid="odoo-hours-start"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Available Until</Label>
                          <Input
                            type="time"
                            value={settings.odoo_available_hours_end || "18:00"}
                            onChange={(e) => handleChange("odoo_available_hours_end", e.target.value)}
                            data-testid="odoo-hours-end"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5 text-slate-400" /> Assigned Salesperson</Label>
                      <Input
                        placeholder="e.g., John - Sales Manager"
                        value={settings.odoo_assigned_user || ""}
                        onChange={(e) => handleChange("odoo_assigned_user", e.target.value)}
                        data-testid="odoo-assigned-user"
                      />
                      <p className="text-xs text-slate-400">Name displayed to prospects on the booking form</p>
                    </div>

                    {/* Test Connection + Save */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!settings.odoo_url || !settings.odoo_db || !settings.odoo_username || !settings.odoo_api_key) {
                            toast.error("Fill in all Odoo credentials first");
                            return;
                          }
                          setOdooTesting(true);
                          setOdooTestResult(null);
                          try {
                            const res = await api.post("/odoo/test-connection", {
                              odoo_url: settings.odoo_url,
                              odoo_db: settings.odoo_db,
                              odoo_username: settings.odoo_username,
                              odoo_api_key: settings.odoo_api_key
                            });
                            setOdooTestResult(res.data);
                            if (res.data.success) {
                              toast.success(`Connected to Odoo v${res.data.version}`);
                            } else {
                              toast.error(res.data.error || "Connection failed");
                            }
                          } catch (err) {
                            setOdooTestResult({ success: false, error: err.response?.data?.detail || "Connection failed" });
                            toast.error("Connection test failed");
                          }
                          setOdooTesting(false);
                        }}
                        disabled={odooTesting}
                        data-testid="odoo-test-btn"
                      >
                        {odooTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                        Test Connection
                      </Button>
                      <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-odoo-btn">
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Odoo Settings
                      </Button>
                    </div>

                    {/* Test Result */}
                    {odooTestResult && (
                      <div className={`p-3 rounded-lg border text-sm ${odooTestResult.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                        <div className="flex items-center gap-2">
                          {odooTestResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                          <span className="font-medium">{odooTestResult.success ? `Connected to Odoo v${odooTestResult.version}` : odooTestResult.error}</span>
                        </div>
                      </div>
                    )}

                    {/* Setup Guide */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <h4 className="font-medium text-blue-800 mb-2 text-sm">How to get your Odoo API Key</h4>
                      <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Log into your Odoo instance</li>
                        <li>Go to <strong>Settings &rarr; Users &rarr; Your User</strong></li>
                        <li>Click <strong>Preferences</strong> tab &rarr; <strong>Account Security</strong></li>
                        <li>Click <strong>"New API Key"</strong> &rarr; copy the key</li>
                        <li>Paste it in the API Key field above</li>
                      </ol>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* SMTP Email Configuration */}
        <Card className="border-slate-200" data-testid="smtp-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-[#0066CC]" />
              Email Notifications (SMTP)
            </CardTitle>
            <CardDescription>Configure SMTP to automatically send email notifications when new leads are captured via any call-to-action</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-[#0066CC]" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Enable Email Notifications</p>
                      <p className="text-xs text-slate-500">When enabled, lead capture forms will trigger email notifications</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.smtp_enabled}
                    onCheckedChange={(val) => handleChange("smtp_enabled", val)}
                    data-testid="smtp-enabled-toggle"
                  />
                </div>

                {/* SMTP Server */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Server className="w-3.5 h-3.5" /> SMTP Host
                    </Label>
                    <Input
                      placeholder="smtp.gmail.com"
                      value={settings.smtp_host}
                      onChange={(e) => handleChange("smtp_host", e.target.value)}
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      placeholder="587"
                      value={settings.smtp_port}
                      onChange={(e) => handleChange("smtp_port", parseInt(e.target.value) || 587)}
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                {/* Credentials */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Username</Label>
                    <Input
                      placeholder="your-email@gmail.com"
                      value={settings.smtp_username}
                      onChange={(e) => handleChange("smtp_username", e.target.value)}
                      data-testid="smtp-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Password / App Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="App password"
                        value={settings.smtp_password}
                        onChange={(e) => handleChange("smtp_password", e.target.value)}
                        data-testid="smtp-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Email Addresses */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Email (Outgoing)</Label>
                    <Input
                      type="email"
                      placeholder="noreply@absolin.com"
                      value={settings.smtp_from_email}
                      onChange={(e) => handleChange("smtp_from_email", e.target.value)}
                      data-testid="smtp-from-email-input"
                    />
                    <p className="text-xs text-slate-500">Email address used as the sender</p>
                  </div>
                  <div className="space-y-2">
                    <Label>To Email (Incoming)</Label>
                    <Input
                      type="email"
                      placeholder="sales@absolin.com"
                      value={settings.smtp_to_email}
                      onChange={(e) => handleChange("smtp_to_email", e.target.value)}
                      data-testid="smtp-to-email-input"
                    />
                    <p className="text-xs text-slate-500">Lead notifications will be sent here</p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <h4 className="font-medium text-amber-800 mb-2 text-sm">Gmail Setup Guide</h4>
                  <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                    <li>Go to Google Account Security 2-Step Verification</li>
                    <li>Generate an App Password (search "App passwords")</li>
                    <li>Host: <code className="bg-amber-100 px-1 rounded">smtp.gmail.com</code>, Port: <code className="bg-amber-100 px-1 rounded">587</code></li>
                    <li>Username: Your Gmail address, Password: The app password</li>
                  </ol>
                </div>

                <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-smtp-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Email Settings
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-[#E6F0FA]">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-slate-900 mb-2">Note</h3>
            <p className="text-sm text-slate-600">
              The "Business Verticals" count is automatically calculated based on the verticals you create.
              The "Technical Experts" count is the sum of all department expert counts.
              The "Branches" count is automatically calculated from the branches you add.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
